import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  Type,
  Injector,
  inject,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges
} from "@angular/core";
import { SortEvent, LazyLoadEvent } from "primeng/api";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { FilterComponent } from "../filter/filter.component";
import {
  trigger,
  state,
  style,
  transition,
  animate,
} from "@angular/animations";
import { PrimeNGSharedModule } from "../../prime/prime-ng.module";
import { CUSTOMER_ONBOARDING_CONSTANTS } from "@core/constants/constants";
import { OverlayPanel } from 'primeng/overlaypanel';
import { ManageAssessmentApiService } from '@core/services/api/manage-assessment-api.service';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute, Router } from "@angular/router";
import { NoLeadingSpaceDirective } from "../../no-leading-space.directive";
import { TruncatePipe } from "../../pipes/truncate.pipe";
import { Subject } from "rxjs";
import { debounceTime, distinctUntilChanged, takeUntil } from "rxjs/operators";
import { TooltipModule } from "primeng/tooltip";

export interface TableColumn {
  field: string;
  header: string;
  sortable?: boolean;
  filterable?: boolean;
  isAction?: boolean;
  isStatus?: boolean;
  isProgress?: boolean;
  type?: string;
  isCustomButton?: boolean;
  hasVersionIcon?: boolean;
  isCustom?: boolean;
  customTemplate?: (rowData: any) => string;
  customCellComponent?: Type<any>;
  maxWidth?: string;
  width?: string;
  showToolTip?: boolean;
  /** If set, the cell value will be truncated to this many characters with ellipsis */
  truncateAt?: number;
  padding?: number;
}
export type FilterType = "multiselect" | "dropdown" | "daterange" | "text";
export interface FilterOption {
  label: string;
  value: string;
}
export interface FilterConfigType {
  key: string;
  label: string;
  type: FilterType;
  options?: FilterOption[];
  optionLabel?: string;
  lazy?: boolean;
  loading?: boolean;
  pageSize?: number;
  virtualScroll?: boolean;
  virtualScrollItemSize?: number;
}

export interface FilterOptionsLazyLoadEvent {
  key: string;
  first?: number;
  rows?: number;
  filter?: string;
}

export interface StatusStyle {
  label: string;
  color: string;
  backgroundColor: string;
}

export interface TableButton {
  label: string;
  icon?: string;
  class?: string;
  action: string; // A unique identifier for the action
  tooltip?: string;
}

@Component({
  selector: "app-table",
  standalone: true,
  imports: [CommonModule, FormsModule, FilterComponent, PrimeNGSharedModule, NoLeadingSpaceDirective, TruncatePipe, TooltipModule],
  templateUrl: "./table.component.html",
  styleUrl: "./table.component.scss",
  animations: [
    trigger("slideToggle", [
      state("visible", style({ height: "*", opacity: 1 })),
      state("hidden", style({ height: "0px", opacity: 0, overflow: "hidden" })),
      transition("visible <=> hidden", animate("300ms ease-in-out")),
    ]),
  ],
})
export class TableComponent implements OnInit, OnDestroy, OnChanges {
  readonly CUSTOMER_ONBOARDING_CONSTANTS = CUSTOMER_ONBOARDING_CONSTANTS;
  @Input() data: any[] = [];
  @Input() columns: TableColumn[] = [];
  @Input() rows: number = 10;
  @Input() showPaginator: boolean = true;
  @Input() showSort: boolean = true;
  @Input() showFilter: boolean = false;
  @Input() isFilterVisible: boolean = false;
  @Input() loading: boolean = false;
  @Input() globalFilterFields: string[] = [];
  @Input() statusStyles: {
    [key: string]: { label: string; color: string; backgroundColor: string };
  } = {};
  @Input() actions: {
    tooltip?: string;
    icon?: string;
    label: string;
    class?: string;
    onClick: (row: any) => void;
  }[] = [];
  @Input() filterConfig: FilterConfigType[] = [];
  @Input() sideFilterConfig?: FilterConfigType[] = [];
  @Input() showGlobalFilter: boolean = true;

  // Two-way binding for the entire filter object
  @Input() filterValue: any;
  @Output() filterValueChange = new EventEmitter<any>();

  // Output for individual filter changes (e.g., from side filters)
  @Output() filterChanged = new EventEmitter<{ key: string; value: any }>();
  @Output() filterOptionsLazyLoad = new EventEmitter<FilterOptionsLazyLoadEvent>();

  @Input() selectionMode?: "single" | "multiple";
  @Input() selectedRows?: any[] = [];
  @Input() showToolTip?: boolean;
  @Input() hideTableHeader: boolean = false;
  @Input() totalRecords: number = 0;
  @Input() assessmentDetails: any = null; // New input to receive assessment details for conditional button visibility
  @Output() globalSearch = new EventEmitter<string>();
  globalFilterValue: string = "";
  @ViewChild("dt") dt: any;
  first = 0;
  @Output() onRowSelect = new EventEmitter<any>();
  @Output() onSort = new EventEmitter<SortEvent>();
  @Output() onPage = new EventEmitter<any>();
  @Output() selectedRowsChange = new EventEmitter<any[]>();
  @Output() searchClicked = new EventEmitter<{ [key: string]: any }>();
  @Output() lazyLoadData = new EventEmitter<LazyLoadEvent>();
  @Output() customBtnClicked = new EventEmitter<any>();
  @Output() viewCertificateEvent = new EventEmitter<{ url: string, fileName: string }>();

  internalFilterValue: { [key: string]: any } = {};
  filtersReady = false;
  // Locks the search input while waiting for a server response
  isSearchLocked = false;

  // Subject for debouncing search input
  private readonly searchSubject = new Subject<string>();
  private readonly destroy$ = new Subject<void>();

  // Use inject function for cleaner dependency injection in components
  private readonly parentInjector = inject(Injector);

  // Cache for custom cell injectors to prevent infinite recreation
  private injectorCache = new Map<any, Injector>();

  currentUserRole: any = "";
  currentUserId: number | null = null;
  isFromActionRoute: boolean = false;

  constructor(private manageAssessmentApiService: ManageAssessmentApiService, private router: Router, private activatedRoute: ActivatedRoute) { }
  ngOnInit() {
    this.internalFilterValue = { ...this.filterValue };
    const storageItem: any = localStorage.getItem("profile");
    this.currentUserRole = JSON.parse(storageItem)?.userRole || "";
    this.currentUserId = JSON.parse(storageItem)?.userId || null;
    // Check if the current URL contains 'fromAction'
    this.isFromActionRoute = this.router.url.includes('fromAction');

    // Set up the debounce for the search input
    this.searchSubject.pipe(
      debounceTime(1000), // 1000ms delay as requested
      distinctUntilChanged(), // Only emit if value changed (including empty)
      takeUntil(this.destroy$) // Clean up on destroy
    ).subscribe(searchTerm => {
      // Lock the search box until parent reports loading=false
      this.isSearchLocked = true;
      if (this.dt) {
        this.dt.filterGlobal(
          searchTerm,
          CUSTOMER_ONBOARDING_CONSTANTS.GLOBAL_FILTER_CONTAINS
        );
      }
      this.globalSearch.emit(searchTerm);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    // When parent completes loading, unlock the search input
    if (changes['loading'] && changes['loading'].currentValue === false) {
      this.isSearchLocked = false;
    }

    // Clear injector cache when data changes to prevent stale injectors
    if (changes['data']) {
      this.injectorCache.clear();
    }
  }

  @ViewChild('op') overlayPanel!: OverlayPanel;
  @ViewChild('versionOp') versionOverlayPanel!: OverlayPanel;

  @Output() versionSelected = new EventEmitter<{ rcaId: string; versionDetail: any }>();

  currentVersions: any[] = [];
  currentRcaId: any | null = null;
  private unsubscribe$ = new Subject<void>();

  private readonly statusStyleMap: Map<string, StatusStyle> = new Map<string, StatusStyle>([
    ["Submitted", { label: "Submitted", color: "white", backgroundColor: "#4E46B4" }],
    ["Draft", { label: "Draft", color: "black", backgroundColor: "#F0D413" }],
    ["Completed", { label: "Completed", color: "white", backgroundColor: "#395C22" }],
    ["Cancelled", { label: "Cancelled", color: "white", backgroundColor: "#AE2A30" }],
    ["Deleted", { label: "Deleted", color: "white", backgroundColor: "#F36F2A" }],
    ["Pending", { label: "Pending", color: "white", backgroundColor: "#F36F2A" }],
    ["Received", { label: "Received", color: "white", backgroundColor: "#395C22" }],
    ["Yet to Start", { label: "Yet to Start", color: "white", backgroundColor: "#11698B" }],
    ["Ongoing", { label: "Ongoing", color: "black", backgroundColor: "#F0D413" }],
    ["Expired", { label: "Expired", color: "white", backgroundColor: "#AE2A30" }],
  ]);

  private readonly defaultStatusStyle: StatusStyle = {
    label: "",
    color: "white",
    backgroundColor: "var(--action-button-grey)",
  };

  @Input() showExportButton: boolean = false;
  @Output() onExportClick = new EventEmitter<void>();

  @Input() showConditionalButton: boolean = false; // No Impact button
  @Input() conditionalButtonConfig: TableButton | null = null; // Configuration for the button
  @Output() conditionalButtonClick = new EventEmitter<void>();
  @Input() customButtonVisibilityFn!: ((rowData: any) => boolean) | undefined;
  @Input() customDetailVisibilityFn!: ((rowData: any) => boolean) | undefined;
  @Input() deleteButtonDisabledFn!: ((rowData: any) => boolean) | undefined;
  @Input() reviseButtonVisibilityFn!: ((rowData: any) => boolean) | undefined;
  @Input() shareButtonDisabledFn!: ((rowData: any) => boolean) | undefined;

  ngAfterViewInit() {
    setTimeout(() => (this.filtersReady = true));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.searchSubject.complete();
  }

  loadDataLazy(event: any) {
    this.first = event.first ?? 0;
    this.rows = event.rows ?? this.rows;
    this.lazyLoadData.emit(event);
  }

  trackByFilterKey(_: number, filter: FilterConfigType) {
    return filter.key;
  }

  onFilterChanged(event: { key: string; value: any }) {
    if (
      event.value === null ||
      (Array.isArray(event.value) && event.value.length === 0)
    ) {
      delete this.internalFilterValue[event.key];
    } else {
      this.internalFilterValue[event.key] = event.value;
    }
    this.filterValueChange.emit(this.internalFilterValue);
  }

  onSearchClick() {
    this.searchClicked.emit(this.internalFilterValue);
  }

  // reset filter only if there are active filters
  resetFilters(): void {
    // Check if there are any filters currently applied
    const hasActiveFilters = Object.keys(this.internalFilterValue).length > 0;

    // Only if we have active filters, proceed with reset
    if (hasActiveFilters) {
      this.internalFilterValue = {};
      this.filterValueChange.emit(this.internalFilterValue);
      this.searchClicked.emit(this.internalFilterValue);
    } else {

    }
  }

  getPaginationStart(): number {
    return this.totalRecords > 0 ? this.first + 1 : 0;
  }

  getPaginationEnd(): number {
    return Math.min(this.first + this.rows, this.totalRecords);
  }

  handlePageChange(event: any) {
    this.first = event.first;
    this.rows = event.rows;
    this.onPage.emit(event);
  }

  // Modified to use the subject for debouncing
  applyGlobalFilter(event: Event) {
    const inputElement = event.target as HTMLInputElement;

    // Normalize to a trimmed string for consistent queries
    const trimmed = inputElement.value.trim();
    if (trimmed === '' && inputElement.value !== '') {
      // If user entered only spaces, normalize the input box
      inputElement.value = '';
    }
    this.globalFilterValue = trimmed;

    // Push the term (even empty) to be handled after 1000ms
    this.searchSubject.next(this.globalFilterValue);
  }

  onTableFiltered(event: any) {
    const filteredRows = event.filteredValue;
    if (!filteredRows || filteredRows.length === 0) {
    } else {
    }
  }
  getStatusStyle(value: string | undefined): StatusStyle {
    if (!value) {
      return { ...this.defaultStatusStyle, label: "" };
    }
    let style: StatusStyle | undefined =
      this.statusStyles[value] || this.statusStyles[value.toLowerCase()];
    if (!style) {
      style = this.statusStyleMap.get(value);
    }
    if (!style) {
      style = {
        label: value,
        color: "white",
        backgroundColor: "var(--action-button-grey)",
      };
    }
    return style;
  }

  // New method to calculate progress bar width
  calculateProgressWidth(progressString: string): string {
    if (!progressString || typeof progressString !== 'string' || progressString === 'N/A') {
      return '0%';
    }
    const parts = progressString.split('/');
    if (parts.length === 2) {
      const current = parseFloat(parts[0]);
      const total = parseFloat(parts[1]);
      if (!isNaN(current) && !isNaN(total) && total > 0) {
        return `${Math.min((current / total) * 100, 100)}%`;
      }
    }
    return '0%';
  }

  toggleFilters() {
    this.isFilterVisible = !this.isFilterVisible;
  }

  isTextTruncated(element: any): boolean {
    const target = element?.target as HTMLElement | undefined;
    return !!target && target.offsetWidth < target.scrollWidth;
  }

  getCustomCellInjector(fieldValue: any): Injector {
    const cacheKey = fieldValue ?? 'null';

    if (this.injectorCache.has(cacheKey)) {
      return this.injectorCache.get(cacheKey)!;
    }

    const valueAsObject = typeof fieldValue === 'object' && fieldValue !== null ? fieldValue : null;
    const certificateUrl = valueAsObject?.url ?? (typeof fieldValue === 'string' ? fieldValue : null);

    const injector = Injector.create({
      providers: [
        { provide: 'certificateUrl', useValue: certificateUrl },
        { provide: 'certificateInfo', useValue: valueAsObject },
        {
          provide: 'viewCertificateCallback',
          useValue: (event: { url: string; fileName: string }) => {
            this.viewCertificateEvent.emit(event);
          },
        },
      ],
      parent: this.parentInjector,
    });

    this.injectorCache.set(cacheKey, injector);
    return injector;
  }

  onCustomToggle(rowData: any, type: string) {
    const actionItems = {
      row: rowData,
      action: type
    }
    this.customBtnClicked.emit(actionItems);
  }

  /** Row that opened the overlay menu; use this inside overlay content so actions target the correct row. */
  overlayContextRow: any = null;

  toggleOverlayPanel(event: Event, rowData: any) {
    this.overlayContextRow = rowData;
    this.overlayPanel.toggle(event);
  }

  toggleVersionOverlayPanel(event: Event, rowData: any) {
    // Check if this is a response form (has response_mapping_id) or an RCA
    const isResponseForm = rowData?.response_mapping_id !== undefined && rowData?.response_mapping_id !== null;

    if (isResponseForm) {
      // Call response version history API
      this.manageAssessmentApiService.getResponseVersionHistory(rowData.response_mapping_id)
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe({
          next: (response) => {
            if (response.history && response.history.length > 0) {
              this.currentVersions = response.history;
              this.currentRcaId = rowData.rca_id;
              this.versionOverlayPanel.toggle(event);
            }
          },
          error: (err: HttpErrorResponse) =>
            console.error("Failed to load versions", err),
        });
    } else {
      // Call RCA version history API
      this.manageAssessmentApiService.getRcaVersionHistory(rowData?.rca_id)
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe({
          next: (response) => {
            if (response.history && response.history.length > 0) {
              this.currentVersions = response.history;
              this.currentRcaId = rowData.rca_id;
              this.versionOverlayPanel.toggle(event);
            }
          },
          error: (err: HttpErrorResponse) =>
            console.error("Failed to load versions", err),
        });
    }
  }

  navigateToVersion(rcaId: string | null) {
    if (rcaId) {
      this.versionSelected.emit({ rcaId: rcaId, versionDetail: this.currentVersions });
      this.versionOverlayPanel.hide();
    }
  }

  emitExportClick(): void {
    this.onExportClick.emit();
  }

  emitConditionalButtonClick(): void {
    this.conditionalButtonClick.emit();
  }

  showActionButton(rowData: any): boolean {
    return rowData?.owner_user_id === this.currentUserId || rowData?.shared_user_ids?.includes(this.currentUserId);
  }

}